'use client';
// SportsGameCard Component - Common card structure for all sports games (NFL, NBA, etc.)
import { memo, useMemo, useState } from 'react';
import { Box, Card } from '@mui/material';

import { EventMarket, EventOutcome, EventResponse } from '@/types';
import {
  findMarket,
  findMarketByLine,
  findMarketByOutcomeId,
  findOutcomeForTeamName,
  formatSportPrice,
  formatTime,
  formatVolume,
  getGameWinner,
  getMarketType,
  getOutcomes,
  getPreferredOutcomeDisplayPrice,
  getSpreadLine,
  hasValidPrice as hasValidOutcomePrice,
  pairTwoWayMoneylineOutcomes,
  parseLiveScore,
  resolveDrawOutcome,
} from '@/utils';
import { BettingGrid, GameCardHeader, LineSelector, TeamInfoColumn } from './subcomponents';

interface SportsGameCardProps {
  event: EventResponse;
  onTeamClick: (event: EventResponse, teamIndex: 1 | 2, isPreviousMarket?: boolean) => void;
  onPropTradeClick?: (
    marketId: string,
    outcomeId: string,
    isBuy: boolean,
    event?: EventResponse,
  ) => void;
  isSelected?: boolean;
  onSelect?: () => void;
  columnVisibility?: {
    hasMoneyline: boolean;
    hasSpread: boolean;
    hasTotal: boolean;
  };
  sportSlug?: string;
  activeOutcomeId?: string;
  isPreviousMarket?: boolean;
}

export function SportsGameCard({
  event,
  onTeamClick,
  onPropTradeClick,
  isSelected = false,
  onSelect,
  columnVisibility = { hasMoneyline: true, hasSpread: true, hasTotal: true },
  sportSlug,
  activeOutcomeId,
  isPreviousMarket = false,
}: SportsGameCardProps) {
  const isLive = !!event.isLive;
  const parsedScore = parseLiveScore(event.score);
  const time = formatTime(event.startTime);
  const volume = `${formatVolume(event.volume)} Vol.`;
  const isEnded = event?.isLive === false;

  const isSpreadActive = useMemo(() => {
    if (!activeOutcomeId) return false;
    return event.markets?.some((m) => {
      const type = (m.sportsMarketType || m.sportsType || '').toLowerCase();
      if (type === 'spread' || type === 'spreads' || type === 'handicap' || type === 'puck line') {
        const outcomes = typeof m.outcomes === 'string' ? JSON.parse(m.outcomes) : m.outcomes || [];
        return outcomes.some((o: any) => o.id === activeOutcomeId);
      }
      return false;
    });
  }, [event.markets, activeOutcomeId]);

  const isTotalActive = useMemo(() => {
    if (!activeOutcomeId) return false;
    return event.markets?.some((m) => {
      const type = (m.sportsMarketType || m.sportsType || '').toLowerCase();
      if (type === 'total' || type === 'totals' || type === 'over/under') {
        const outcomes = typeof m.outcomes === 'string' ? JSON.parse(m.outcomes) : m.outcomes || [];
        return outcomes.some((o: any) => o.id === activeOutcomeId);
      }
      return false;
    });
  }, [event.markets, activeOutcomeId]);

  // 1. Extract unique lines for spreads and totals separately
  const { spreadLines, totalLines } = useMemo(() => {
    if (isLive) return { spreadLines: [], totalLines: [] };
    const sLines = new Set<string | number>();
    const tLines = new Set<string | number>();

    event.markets?.forEach((m) => {
      const type = (m.sportsMarketType || m.sportsType || '').toLowerCase();
      const threshold = m.line;
      if (threshold === undefined || threshold === null || String(threshold) === '') return;

      // Normalize to number if possible to avoid string vs number duplicates
      const numValue = Number(threshold);
      const normalizedThreshold = isNaN(numValue) ? threshold : numValue;

      if (type === 'spread' || type === 'spreads') {
        sLines.add(normalizedThreshold);
      } else if (type === 'total' || type === 'totals') {
        tLines.add(normalizedThreshold);
      }
    });

    return {
      spreadLines: Array.from(sLines).sort((a, b) => Number(a) - Number(b)),
      totalLines: Array.from(tLines).sort((a, b) => Number(a) - Number(b)),
    };
  }, [event.markets, isLive]);

  const activeLines = isSpreadActive ? spreadLines : totalLines;

  // 2. State for currently selected line
  const [selectedLine, setSelectedLine] = useState<string | number | undefined>(() => {
    const initialLines = spreadLines.length > 0 ? spreadLines : totalLines;
    if (initialLines.length > 0) {
      // Find the "most standard" line.
      const balanced = initialLines.filter((l) => Number(l) % 1 !== 0 || Math.abs(Number(l)) < 10);
      return balanced.length > 0
        ? balanced[Math.floor(balanced.length / 2)]
        : initialLines[Math.floor(initialLines.length / 2)];
    }
    return undefined;
  });

  // 3. Sync selectedLine if markets change or if user clicks an outcome
  const [prevOutcomeId, setPrevOutcomeId] = useState<string | undefined>(activeOutcomeId);

  if (activeOutcomeId !== prevOutcomeId) {
    setPrevOutcomeId(activeOutcomeId);

    if (activeOutcomeId) {
      const activeMarket = event.markets?.find((m) => {
        const outcomes = typeof m.outcomes === 'string' ? JSON.parse(m.outcomes) : m.outcomes || [];
        return outcomes.some((o: any) => o.id === activeOutcomeId);
      });

      const marketThreshold = activeMarket?.line;

      if (marketThreshold !== undefined && marketThreshold !== selectedLine) {
        const type = (
          activeMarket?.sportsMarketType ||
          activeMarket?.sportsType ||
          ''
        ).toLowerCase();
        if (
          type === 'spread' ||
          type === 'spreads' ||
          type === 'handicap' ||
          type === 'puck line' ||
          type === 'total' ||
          type === 'totals' ||
          type === 'over/under'
        ) {
          setSelectedLine(marketThreshold);
        }
      }
    }
  }

  // Adjust selectedLine if activeLines changed and current line is invalid
  if (
    activeLines.length > 0 &&
    (selectedLine === undefined || !activeLines.some((l) => String(l) === String(selectedLine)))
  ) {
    const newLine = activeLines[Math.floor(activeLines.length / 2)];
    if (selectedLine !== newLine) {
      setSelectedLine(newLine);
    }
  }

  // 4. Resolve Markets and Outcomes
  const activeMarket = useMemo(
    () => findMarketByOutcomeId(event.markets, activeOutcomeId),
    [event.markets, activeOutcomeId],
  );

  const getEffectiveMarket = (type: string) => {
    const normalizedType = type.toLowerCase();
    if (activeMarket && getMarketType(activeMarket).includes(normalizedType)) {
      return activeMarket;
    }
    if (selectedLine && (normalizedType.includes('spread') || normalizedType.includes('total'))) {
      return findMarketByLine(event, type, selectedLine) || findMarket(event, type);
    }
    return findMarket(event, type);
  };

  const drawMarket = getEffectiveMarket('draw');
  const hasDrawMarket = !!drawMarket;
  const getMarketForTeam = (teamName?: string) => {
    if (!teamName) return undefined;
    const lowerName = teamName.toLowerCase().trim();
    return event.markets?.find((m) => {
      const type = getMarketType(m);
      const title = ((m as any).groupItemTitle || '').toLowerCase();
      return type.includes('moneyline') && title.includes(lowerName) && !title.includes('draw');
    });
  };
  const homeMarket = getMarketForTeam(event.homeTeam?.name) || getEffectiveMarket('moneyline');
  const awayMarket = getMarketForTeam(event.awayTeam?.name);
  const moneylineMarket = hasDrawMarket ? homeMarket : getEffectiveMarket('moneyline');
  const spreadsMarket = getEffectiveMarket('spread');
  const totalsMarket = getEffectiveMarket('total');

  const mlOutcomes = getOutcomes(moneylineMarket);
  const pairedMl = pairTwoWayMoneylineOutcomes(
    mlOutcomes,
    event.homeTeam?.name,
    event.awayTeam?.name,
  );
  const spreadOutcomes = getOutcomes(spreadsMarket);
  const totalOutcomes = getOutcomes(totalsMarket);
  const drawOutcomes = getOutcomes(drawMarket);

  const winnerResult =
    isEnded && parsedScore
      ? getGameWinner(
          parsedScore.home,
          parsedScore.away,
          event.homeTeam?.name || 'Home',
          event.awayTeam?.name || 'Away',
        )
      : null;

  const solveTeamData = (m: EventMarket | undefined, isAway: boolean, tName?: string) => {
    if (!m) return { price: '', id: undefined };
    const outcomes = getOutcomes(m);
    const tNameLower = tName?.toLowerCase().trim();
    const isIndependent = !!awayMarket;
    let outcome: EventOutcome | undefined;

    if (isIndependent) {
      outcome =
        outcomes.find((o) => o.name.toLowerCase() === 'yes') ||
        outcomes.find((o) => o.name.toLowerCase().includes(tNameLower || '')) ||
        outcomes[0];
    } else {
      outcome =
        outcomes.find((o) => o.name.toLowerCase().includes(tNameLower || '')) ||
        outcomes[isAway ? 1 : 0];
    }

    return {
      price: formatSportPrice(
        getPreferredOutcomeDisplayPrice(outcome?.bestAsk, outcome?.price, {
          bestBid: outcome?.bestBid ?? (m as any)?.bestBid,
          isClosed: (m as any)?.closed === true || (m as any)?.closed === 'true',
        }),
      ),
      id: outcome?.id,
    };
  };

  const team1Data = hasDrawMarket
    ? solveTeamData(homeMarket, false, event.homeTeam?.name)
    : {
        price: formatSportPrice(
          getPreferredOutcomeDisplayPrice(pairedMl.home?.bestAsk, pairedMl.home?.price, {
            bestBid: pairedMl.home?.bestBid ?? (moneylineMarket as any)?.bestBid,
            isClosed:
              (moneylineMarket as any)?.closed === true ||
              (moneylineMarket as any)?.closed === 'true',
          }),
        ),
        id: pairedMl.home?.id,
      };
  const team2Data = hasDrawMarket
    ? solveTeamData(awayMarket || homeMarket, true, event.awayTeam?.name)
    : {
        price: formatSportPrice(
          getPreferredOutcomeDisplayPrice(pairedMl.away?.bestAsk, pairedMl.away?.price, {
            bestBid: pairedMl.away?.bestBid ?? (moneylineMarket as any)?.bestBid,
            isClosed:
              (moneylineMarket as any)?.closed === true ||
              (moneylineMarket as any)?.closed === 'true',
          }),
        ),
        id: pairedMl.away?.id,
      };

  const team1 = {
    abbr: event.homeTeam?.abbreviation?.toUpperCase() || '',
    name: event.homeTeam?.name || 'Team 1',
    record: event.homeTeam?.score || '',
    color: event.homeTeam?.color || '#e8b046',
    price: team1Data.price,
    id: team1Data.id,
    marketId: hasDrawMarket ? homeMarket?.id : moneylineMarket?.id,
    logo: event.homeTeam?.logo,
    liveScore: isLive || isEnded ? parsedScore?.home : null,
    isWinner: winnerResult?.includes(event.homeTeam?.name || 'Home'),
  };

  const team2 = {
    abbr: event.awayTeam?.abbreviation?.toUpperCase() || '',
    name: event.awayTeam?.name || 'Team 2',
    record: event.awayTeam?.score || '',
    color: event.awayTeam?.color || '#c24632',
    price: team2Data.price,
    id: team2Data.id,
    marketId: hasDrawMarket ? (awayMarket || homeMarket)?.id : moneylineMarket?.id,
    logo: event.awayTeam?.logo,
    liveScore: isLive || isEnded ? parsedScore?.away : null,
    isWinner: winnerResult?.includes(event.awayTeam?.name || 'Away'),
  };

  const homeSpreadOutcome = findOutcomeForTeamName(spreadOutcomes, event.homeTeam?.name, 0);
  const awaySpreadOutcome = findOutcomeForTeamName(spreadOutcomes, event.awayTeam?.name, 1);

  const spread1 =
    spreadsMarket && homeSpreadOutcome
      ? {
          line: getSpreadLine(spreadsMarket, event.homeTeam?.name, false),
          price: formatSportPrice(
            getPreferredOutcomeDisplayPrice(homeSpreadOutcome?.bestAsk, homeSpreadOutcome?.price, {
              bestBid: homeSpreadOutcome?.bestBid ?? (spreadsMarket as any)?.bestBid,
              isClosed:
                (spreadsMarket as any)?.closed === true ||
                (spreadsMarket as any)?.closed === 'true',
            }),
          ),
          id: homeSpreadOutcome?.id,
          marketId: spreadsMarket.id,
        }
      : undefined;

  const spread2 =
    spreadsMarket && awaySpreadOutcome
      ? {
          line: getSpreadLine(spreadsMarket, event.awayTeam?.name, true),
          price: formatSportPrice(
            getPreferredOutcomeDisplayPrice(awaySpreadOutcome?.bestAsk, awaySpreadOutcome?.price, {
              bestBid: awaySpreadOutcome?.bestBid ?? (spreadsMarket as any)?.bestBid,
              isClosed:
                (spreadsMarket as any)?.closed === true ||
                (spreadsMarket as any)?.closed === 'true',
            }),
          ),
          id: awaySpreadOutcome?.id,
          marketId: spreadsMarket.id,
        }
      : undefined;

  const overOutcome =
    totalOutcomes.find((o) => o.name?.toLowerCase().includes('over')) || totalOutcomes[0];
  const underOutcome =
    totalOutcomes.find((o) => o.name?.toLowerCase().includes('under')) || totalOutcomes[1];

  const totalOver =
    totalsMarket && overOutcome
      ? {
          line: (totalsMarket as EventMarket).line || '',
          price: formatSportPrice(
            getPreferredOutcomeDisplayPrice(overOutcome?.bestAsk, overOutcome?.price, {
              bestBid: overOutcome?.bestBid ?? (totalsMarket as any)?.bestBid,
              isClosed:
                (totalsMarket as any)?.closed === true || (totalsMarket as any)?.closed === 'true',
            }),
          ),
          id: overOutcome?.id,
          marketId: totalsMarket.id,
        }
      : undefined;

  const totalUnder =
    totalsMarket && underOutcome
      ? {
          line: (totalsMarket as EventMarket).line || '',
          price: formatSportPrice(
            getPreferredOutcomeDisplayPrice(underOutcome?.bestAsk, underOutcome?.price, {
              bestBid: underOutcome?.bestBid ?? (totalsMarket as any)?.bestBid,
              isClosed:
                (totalsMarket as any)?.closed === true || (totalsMarket as any)?.closed === 'true',
            }),
          ),
          id: underOutcome?.id,
          marketId: totalsMarket.id,
        }
      : undefined;

  const { pricing: drawData } = resolveDrawOutcome(event);

  // Use shared validator: accepts price OR orderbook-derived bestBid/bestAsk.
  // List cards price buttons rely on bestBid/bestAsk for many sports markets.
  const hasValidSpread = spreadOutcomes.length > 0 && hasValidOutcomePrice(spreadOutcomes);
  const hasValidTotal = totalOutcomes.length > 0 && hasValidOutcomePrice(totalOutcomes);

  // If both market types are enabled for the sport, and either has data, show both columns for a consistent 3-market grid.
  const shouldShowBoth =
    columnVisibility.hasSpread && columnVisibility.hasTotal && (hasValidSpread || hasValidTotal);

  const gameViewCount = event?.markets?.length || 0;

  const effectiveVisibility = {
    hasMoneyline:
      columnVisibility.hasMoneyline && mlOutcomes.length > 0 && hasValidOutcomePrice(mlOutcomes),
    hasSpread: columnVisibility.hasSpread && (shouldShowBoth || hasValidSpread),
    hasTotal: columnVisibility.hasTotal && (shouldShowBoth || hasValidTotal),
    hasDraw: !!drawData && columnVisibility.hasMoneyline,
  };

  const handleLineSelect = (newLine: string | number) => {
    setSelectedLine(newLine);

    // Auto-select equivalent outcome in the new market type if something is already active
    if (activeOutcomeId && onPropTradeClick) {
      const activeMarket = event.markets?.find((m) => {
        const outcomes = typeof m.outcomes === 'string' ? JSON.parse(m.outcomes) : m.outcomes || [];
        return outcomes.some((o: any) => o.id === activeOutcomeId);
      });

      if (activeMarket) {
        const type = (activeMarket.sportsMarketType || activeMarket.sportsType || '').toLowerCase();
        const activeOutcomes =
          typeof activeMarket.outcomes === 'string'
            ? JSON.parse(activeMarket.outcomes)
            : activeMarket.outcomes || [];
        const activeOutcome = activeOutcomes.find((o: any) => o.id === activeOutcomeId);

        if (activeOutcome) {
          const newMarket = findMarketByLine(event, type, newLine);
          if (newMarket) {
            const newOutcomes =
              typeof newMarket.outcomes === 'string'
                ? JSON.parse(newMarket.outcomes)
                : newMarket.outcomes || [];

            // Robust outcome migration: find matching outcome by name or type label
            const activeName = (activeOutcome.name || '').toLowerCase();
            const matchingOutcome = newOutcomes.find((o: any) => {
              const newName = (o.name || '').toLowerCase();
              return newName === activeName && activeName !== '';
            });

            if (matchingOutcome) {
              onPropTradeClick(newMarket.id, matchingOutcome.id, true, event);
            }
          }
        }
      }
    }
  };

  const handleCardClick = () => {
    if (onSelect) {
      onSelect();
    } else if (onTeamClick) {
      onTeamClick(event, 1, isPreviousMarket);
    }
  };

  const showLineSelector =
    !isLive && !isPreviousMarket && activeLines.length > 1 && (isSpreadActive || isTotalActive);

  const resolutionSource = event.markets?.[0]?.resolutionSource;

  const isSpreadOutcomeActive =
    !!activeOutcomeId && spreadOutcomes.some((o) => o.id === activeOutcomeId);
  const isTotalOutcomeActive =
    !!activeOutcomeId && totalOutcomes.some((o) => o.id === activeOutcomeId);

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        borderRadius: 2,
        border: '1px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        bgcolor: 'background.paper',
        boxShadow: 'none',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s',
        '&:hover': { borderColor: isSelected ? 'primary.main' : 'action.focus' },
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <GameCardHeader
        gameId={event.id}
        isLive={isLive}
        ended={isEnded}
        period={event.period}
        elapsed={event.elapsed}
        sportSlug={sportSlug || 'live'}
        gameViewCount={gameViewCount}
        time={time}
        volume={volume}
        isPreviousMarket={isPreviousMarket}
        resolutionSource={resolutionSource}
      />

      <Box sx={{ p: 2, pt: 0 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 2.5, md: 2 },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
          }}
        >
          <TeamInfoColumn
            event={event}
            team1={team1}
            team2={team2}
            isLive={isLive}
            isPreviousMarket={isPreviousMarket}
          />

          {!isPreviousMarket && (
            <BettingGrid
              event={event}
              onTeamClick={onTeamClick}
              onPropTradeClick={onPropTradeClick}
              moneylineMarket={moneylineMarket}
              spreadsMarket={spreadsMarket}
              totalsMarket={totalsMarket}
              isSpreadActive={isSpreadOutcomeActive}
              isTotalActive={isTotalOutcomeActive}
              team1={team1}
              team2={team2}
              spread1={spread1}
              spread2={spread2}
              totalOver={totalOver}
              totalUnder={totalUnder}
              columnVisibility={effectiveVisibility}
              activeOutcomeId={activeOutcomeId}
              selectedLine={selectedLine}
              drawData={drawData}
            />
          )}
        </Box>
      </Box>
      {/* Line Selector for non-live sports with multiple lines */}
      {showLineSelector && (
        <LineSelector
          allLines={activeLines}
          selectedLine={selectedLine}
          onLineSelect={handleLineSelect}
        />
      )}
    </Card>
  );
}

export default memo(SportsGameCard);
